import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  MailCheck,
  Send,
} from 'lucide-react';

import authService from '../../services/authService';
import './VerifyEmail.css';

type VerifyState = 'verifying' | 'success' | 'error' | 'idle';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState<VerifyState>('idle');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resentMessage, setResentMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage(
        'This verification link is invalid or incomplete. Use the link from your verification email.'
      );
      return;
    }

    let cancelled = false;
    (async () => {
      setState('verifying');
      try {
        const res = await authService.verifyEmail(token);
        if (!cancelled) {
          setState('success');
          setMessage(res.message || 'Email verified successfully. You can now log in.');
        }
      } catch (err: any) {
        if (!cancelled) {
          setState('error');
          setMessage(
            err?.detail ||
              err?.message ||
              'This verification link is invalid or has expired. You can request a new link below.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleResend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    setResending(true);
    setResentMessage('');
    try {
      await authService.resendVerification(email.trim().toLowerCase());
      setResentMessage('If that email exists and is unverified, a new verification link has been sent.');
      setEmail('');
    } catch {
      setResentMessage('Please try again after a moment.');
    } finally {
      setResending(false);
    }
  };

  const renderBody = () => {
    if (state === 'verifying') {
      return (
        <div className="login-form-container">
          <button type="button" className="back-to-roles" onClick={() => navigate('/login')}>
            <ArrowLeft size={16} /> Back
          </button>

          <div className="verify-email-icon">
            <MailCheck size={32} />
          </div>

          <h2 className="login-title">Verifying your email</h2>
          <p className="login-subtitle">Please wait while we confirm your address.</p>

          <div className="login-button" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12 }}>
            <Loader2 size={20} className="spinner" /> Verifying...
          </div>
        </div>
      );
    }

    if (state === 'success') {
      return (
        <div className="login-form-container">
          <button type="button" className="back-to-roles" onClick={() => navigate('/login')}>
            <ArrowLeft size={16} /> Back
          </button>

          <div className="login-success">
            <CheckCircle2 size={24} />
            <div>
              <strong>Email Verified</strong>
              <p>{message}</p>
            </div>
          </div>

          <button type="button" className="login-button" onClick={() => navigate('/login', { replace: true })}>
            Continue to Sign In
          </button>

          <div className="login-footer">
            <p>
              Already logged in? <Link to="/login" className="register-link">Sign In</Link>
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="login-form-container">
        <button type="button" className="back-to-roles" onClick={() => navigate('/login')}>
          <ArrowLeft size={16} /> Back
        </button>

        <div className="login-error">
          <AlertCircle size={18} />
          <span>{message}</span>
        </div>

        <h2 className="login-title">Resend Verification Link</h2>
        <p className="login-subtitle">
          Enter your email and we will send you a fresh verification link.
        </p>

        <form onSubmit={handleResend} className="login-form">
          <div className="form-group">
            <label>Email</label>
            <div className="input-wrapper">
              <Send size={18} className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setResentMessage('');
                }}
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          {resentMessage && <p className="login-subtitle">{resentMessage}</p>}

          <button type="submit" className="login-button" disabled={resending}>
            {resending ? (
              <span className="button-loading">
                <Loader2 size={20} className="spinner" /> Sending...
              </span>
            ) : (
              'Resend Verification Email'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Remember your password? <Link to="/login" className="register-link">Sign In</Link>
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="login-page">
      <div className="login-bg-shape shape-1" />
      <div className="login-bg-shape shape-2" />
      <div className="login-bg-shape shape-3" />

      <div className="login-container">
        <div className="login-right">{renderBody()}</div>
      </div>
    </div>
  );
};

export default VerifyEmail;